import { get } from 'lodash';
import * as React from 'react';
import { useFormikContext } from 'formik';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import useIsMobile from '../../utils/useIsMobile';
import { useDisabled } from './providers/DisableProvider';
import useGutterBottom from './utils/useGutterBottom';

type Props = TextFieldProps & {
  name: string;
  select?: never;
  errorGutterBottom?: boolean;
  multiple?: boolean;
  acceptFiles?: 'images' | 'all' | 'videos';
  /** Raw `accept` attribute, e.g. ".pdf,.docx". Takes precedence over `acceptFiles`. */
  accept?: string;
};

const FileInput = ({
  name,
  errorGutterBottom,
  multiple,
  acceptFiles,
  accept: acceptOverride,
  ...props
}: Props) => {
  const { setFieldValue, setFieldTouched, errors, touched, values } =
    useFormikContext<any>();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const value = get(values, name);
  const isEmpty = !value || (Array.isArray(value) && !value.length);
  const isError = !!(get(touched, name) && get(errors, name));
  const isMobile = useIsMobile();
  const { getDisabled } = useDisabled();
  const gutterSize = useGutterBottom();

  // a reset form empties the value, the native input has to follow
  React.useEffect(() => {
    if (isEmpty && inputRef.current) inputRef.current.value = '';
  }, [isEmpty]);

  let accept: string | undefined = acceptOverride;
  switch (!accept && acceptFiles) {
    case 'images':
      accept = 'image/*';
      break;
    case 'videos':
      accept = 'video/*';
  }
  return (
    <TextField
      name={name}
      inputRef={inputRef}
      variant="outlined"
      type="file"
      size={isMobile ? 'small' : 'medium'}
      disabled={getDisabled?.(name)}
      error={!!(get(touched, name) && get(errors, name))}
      helperText={
        isError ? (get(errors, name) as string) : props.helperText || ''
      }
      sx={{
        paddingBottom: errorGutterBottom ? (isError ? 0 : gutterSize) : 0,
        ...props.sx,
      }}
      {...props}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange?.(event);
        const files = event.currentTarget.files || undefined;
        const value = multiple && files ? Array.from(files) : files?.[0];
        setFieldValue(name, value);
      }}
      slotProps={{
        inputLabel: { shrink: true },
        htmlInput: {
          multiple,
          accept: accept,
          'aria-label': name,
        },
        ...props.slotProps,
      }}
      onBlur={(event) => {
        props.onBlur?.(event);
        setFieldTouched(name, true);
      }}
    />
  );
};

export default FileInput;
